import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect, useMemo } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../../models/Authorization"
import {
  DeviceSoftwarePackageFirmwareConstraintGet,
  DeviceSoftwarePackageFirmwareConstraintPut,
} from "../../../../../models/DeviceSoftwarePackageFirmwareConstraint"
import { UserInfo } from "../../../../../models/User"
import { createDeviceSoftwarePackageFirmwareConstraintValidationSchema } from "../../../../../components/Helpers"

const validationSchema = createDeviceSoftwarePackageFirmwareConstraintValidationSchema()

export function DeviceSoftwarePackageFirmwareConstraintDetails(props: {
  readonly softwarePackageFirmwareConstraint: DeviceSoftwarePackageFirmwareConstraintGet
  readonly deviceId: string
  readonly user: UserInfo
  readonly setSoftwarePackageFirmwareConstraint: (
    updatedSoftwarePackageFirmwareConstraint: DeviceSoftwarePackageFirmwareConstraintGet
  ) => void
}) {
  const {
    softwarePackageFirmwareConstraint,
    deviceId,
    user,
    setSoftwarePackageFirmwareConstraint,
  } = props

  const returnURL = `/devices/${deviceId}#software-package-firmware-constraints`

  const formMethods = useForm<DeviceSoftwarePackageFirmwareConstraintPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      softwarePackageEditionCode:
        softwarePackageFirmwareConstraint.softwarePackageEditionCode,
      mainboardMinSoftwareVersion:
        softwarePackageFirmwareConstraint.mainboardMinSoftwareVersion,
      hmiMinSoftwareVersion: softwarePackageFirmwareConstraint.hmiMinSoftwareVersion,
    })
  }, [softwarePackageFirmwareConstraint, reset])

  const onSubmit = useCallback(
    (values: DeviceSoftwarePackageFirmwareConstraintPut) =>
      digitalManagerApi
        .put<DeviceSoftwarePackageFirmwareConstraintGet>(
          `/devices/${deviceId}/softwarePackageFirmwareConstraints/${softwarePackageFirmwareConstraint.id}`,
          values
        )
        .then((res) => {
          toast.success("Software package firmware constraint updated")
          setSoftwarePackageFirmwareConstraint(res.data)
        })
        .catch(() => {
          toast.error("Cannot update software package firmware constraint")
          throw new Error("Cannot update software package firmware constraint")
        }),
    [deviceId, softwarePackageFirmwareConstraint.id, setSoftwarePackageFirmwareConstraint]
  )

  const canWrite = useMemo(
    () =>
      hasUserAccessToFeature(
        user.authorizations,
        DimaFeatureShortNameEnum.DM,
        AccessLevel.Write
      ),
    [user.authorizations]
  )

  const handleTextFieldChange = useCallback(
    (
      field: ControllerRenderProps<
        DeviceSoftwarePackageFirmwareConstraintPut,
        | "softwarePackageEditionCode"
        | "mainboardMinSoftwareVersion"
        | "hmiMinSoftwareVersion"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderSoftwarePackageEditionCodeTextField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<
        DeviceSoftwarePackageFirmwareConstraintPut,
        "softwarePackageEditionCode"
      >
    }) => (
      <MUITextField
        id="softwarePackageEditionCode"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.softwarePackageEditionCode?.message === "string"
            ? {
                softwarePackageEditionCode: {
                  message: errors.softwarePackageEditionCode.message,
                },
              }
            : undefined
        }
      />
    ),
    [canWrite, errors.softwarePackageEditionCode, handleTextFieldChange]
  )

  const renderMainboardMinSoftwareVersionTextField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<
        DeviceSoftwarePackageFirmwareConstraintPut,
        "mainboardMinSoftwareVersion"
      >
    }) => (
      <MUITextField
        id="mainboardMinSoftwareVersion"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        placeholder="XX.XX.XX"
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.mainboardMinSoftwareVersion?.message === "string"
            ? {
                mainboardMinSoftwareVersion: {
                  message: errors.mainboardMinSoftwareVersion.message,
                },
              }
            : undefined
        }
      />
    ),
    [canWrite, errors.mainboardMinSoftwareVersion, handleTextFieldChange]
  )

  const renderHmiMinSoftwareVersionTextField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<
        DeviceSoftwarePackageFirmwareConstraintPut,
        "hmiMinSoftwareVersion"
      >
    }) => (
      <MUITextField
        id="hmiMinSoftwareVersion"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        placeholder="XX.XX.XX"
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.hmiMinSoftwareVersion?.message === "string"
            ? { hmiMinSoftwareVersion: { message: errors.hmiMinSoftwareVersion.message } }
            : undefined
        }
      />
    ),
    [canWrite, errors.hmiMinSoftwareVersion, handleTextFieldChange]
  )

  if (!softwarePackageFirmwareConstraint) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<DeviceSoftwarePackageFirmwareConstraintPut> =
    {
      formMethods,
      canWrite,
      inlineEdit: true,
      listItems: [
        {
          id: 0,
          label: "Software Package Edition Code",
          displayItem: (
            <div style={{ width: "100%" }}>
              {softwarePackageFirmwareConstraint.softwarePackageEditionCode}
            </div>
          ),
          editItem: (
            <Controller
              name="softwarePackageEditionCode"
              control={control}
              render={renderSoftwarePackageEditionCodeTextField}
            />
          ),
        },
        {
          id: 1,
          label: "Mainboard Min SW Version",
          displayItem: (
            <div style={{ width: "100%" }}>
              {softwarePackageFirmwareConstraint.mainboardMinSoftwareVersion}
            </div>
          ),
          editItem: (
            <Controller
              name="mainboardMinSoftwareVersion"
              control={control}
              render={renderMainboardMinSoftwareVersionTextField}
            />
          ),
        },
        {
          id: 2,
          label: "HMI Min SW Version",
          displayItem: (
            <div style={{ width: "100%" }}>
              {softwarePackageFirmwareConstraint.hmiMinSoftwareVersion}
            </div>
          ),
          editItem: (
            <Controller
              name="hmiMinSoftwareVersion"
              control={control}
              render={renderHmiMinSoftwareVersionTextField}
            />
          ),
        },
      ],
    }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
